import { AskTextInput } from "./AskTextInput";
import { useEffect, useLayoutEffect, useRef, useState, type TextareaHTMLAttributes } from "react";
import { fetchBrief, type BriefResponse } from "../api";
import { useAskMentions } from "../lib/useAskMentions";
import type { AskMention } from "../../../../backend/src/shared/askMentions";
import { completedMentionParts } from "../lib/completedMentionParts";
import { mentionFragment } from "../lib/mentionFragment";
import { C } from "../tokens";

/** Ask page and shell composers share Home's workspace-scoped mention sources. */
export function AskMentionInput({ search, placement, onValue, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & {
  search: string;
  placement: "above" | "below";
  onValue: (value: string) => void;
}) {
  const input = useRef<HTMLTextAreaElement>(null);
  const [completedMentions, setCompletedMentions] = useState<string[]>([]);
  const [caret, setCaret] = useState(0);
  const [focused, setFocused] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [index, setIndex] = useState(0);
  const [context, setContext] = useState<{ search: string; data: BriefResponse | null } | null>(null);
  const data = context?.search === search ? context.data : null;
  const value = String(props.value ?? "");
  useEffect(() => { setCompletedMentions([]); }, [search]);
  useEffect(() => {
    setCompletedMentions(current => current.filter(name => value.includes(`@${name}`)));
  }, [value]);
  // Committing the completed draft moves the caret to its end. Put it back
  // after the inserted name in that same commit. A later animation frame is
  // not "right after": it can run once the next draft is typed, and would
  // drop that draft's caret at this completion's offset.
  const completion = useRef<{ value: string; caret: number } | null>(null);
  useLayoutEffect(() => {
    const placed = completion.current;
    completion.current = null;
    if (!placed || placed.value !== value) return;
    input.current?.focus();
    input.current?.setSelectionRange(placed.caret, placed.caret);
  }, [value]);
  const displayedValue = completedMentionParts(value, completedMentions);
  const fragment = mentionFragment(value, caret, completedMentions);
  const open = focused && !dismissed && !props.disabled && fragment !== null;
  // The shell mounts this on every page. Load mention sources only when
  // needed, and keep them scoped to the current workspace/project search.
  const needsContext = open && context?.search !== search;
  useEffect(() => {
    if (!needsContext) return;
    let live = true;
    void fetchBrief(search).then(
      data => { if (live) setContext({ search, data }); },
      () => { if (live) setContext({ search, data: null }); },
    );
    return () => { live = false; };
  }, [search, needsContext]);
  const results = useAskMentions(data?.sites ?? [], open ? fragment?.query ?? null : null, search, [
    ...(data?.subsBehind ?? []).map(company => company.company),
    ...[...(data?.stalled ?? []), ...(data?.moved ?? [])].map(row => row.assigneeCompany),
  ]);
  const active = Math.min(index, Math.max(0, results.choices.length - 1));
  useEffect(() => { setIndex(0); }, [fragment?.query, search]);
  useEffect(() => {
    if (open) document.getElementById(`ask-mention-${active}`)?.scrollIntoView?.({ block: "nearest" });
  }, [active, open]);
  function select(mention: AskMention) {
    if (!fragment) return;
    const { at } = fragment;
    const next = `${value.slice(0, at)}@${mention.name} ${value.slice(caret)}`;
    const nextCaret = at + mention.name.length + 2;
    completion.current = { value: next, caret: nextCaret };
    setCompletedMentions(current => current.includes(mention.name) ? current : [...current, mention.name]);
    onValue(next);
    setCaret(nextCaret);
    setDismissed(true);
  }
  return <div style={{ position: "relative", display: "flex", flexDirection: "column" }}>
    <AskTextInput {...props} ref={input} parts={displayedValue}
      role="combobox" aria-autocomplete="list" aria-expanded={open}
      aria-controls={open ? "ask-mentions" : undefined}
      aria-activedescendant={open && results.choices.length ? `ask-mention-${active}` : undefined}
      onFocus={event => { setFocused(true); setCaret(event.currentTarget.selectionStart ?? value.length); props.onFocus?.(event); }}
      onBlur={event => { setFocused(false); props.onBlur?.(event); }}
      onInput={event => {
        setCaret(event.currentTarget.selectionStart ?? event.currentTarget.value.length);
        setDismissed(false);
        props.onInput?.(event);
      }}
      onClick={event => { setCaret(event.currentTarget.selectionStart ?? value.length); setDismissed(false); }}
      onSelect={event => setCaret(event.currentTarget.selectionStart ?? value.length)}
      onKeyDown={event => {
        if (open && event.key === "Escape") { event.preventDefault(); setDismissed(true); return; }
        if (open && results.choices.length) {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            setIndex((active + (event.key === "ArrowDown" ? 1 : -1) + results.choices.length) % results.choices.length);
            return;
          }
          if (event.key === "Enter" || event.key === "Tab") {
            event.preventDefault(); select(results.choices[active]!); return;
          }
        }
        props.onKeyDown?.(event);
      }} />
    {open && <div id="ask-mentions" data-testid="ask-mentions" role="listbox" aria-label="Projects, companies, and people"
      style={{ position: "absolute", ...(placement === "above" ? { bottom: "100%" } : { top: "100%" }), left: 12,
        zIndex: 30, width: "min(340px, calc(100% - 24px))", maxHeight: "min(320px, 40vh)", overflowY: "auto",
        padding: 6, border: `1px solid ${C.line2}`, borderRadius: 14, background: C.surface, boxShadow: `0 12px 30px ${C.line}` }}>
      <div style={{ padding: "6px 9px", fontSize: 12, color: C.sub }}>Mention a project, company, or person</div>
      {(context?.search !== search || results.loading) && <div role="status">Searching directories…</div>}
      {((context?.search === search && !data) || results.failed) && <div role="alert">Some project directories could not be searched. Try again.</div>}
      {data && !results.loading && !results.failed && !results.choices.length && <div>No matching entries.</div>}
      {results.choices.map((mention, i) => <button key={`${mention.kind}:${mention.id}`} id={`ask-mention-${i}`}
        type="button" role="option" aria-selected={i === active} tabIndex={-1}
        onMouseDown={event => event.preventDefault()} onClick={() => select(mention)}
        style={{ display: "flex", justifyContent: "space-between", gap: 12, width: "100%", padding: "8px 10px",
          border: 0, borderRadius: 9, background: i === active ? C.railRow : "transparent", color: C.ink, textAlign: "left", cursor: "pointer", font: "inherit" }}>
        <span style={{ minWidth: 0, overflowWrap: "anywhere" }}>{mention.name}{mention.detail && <small style={{ display: "block", color: C.sub }}>{mention.detail}</small>}</span>
        <small style={{ color: C.sub }}>{mention.kind}</small>
      </button>)}
      <div style={{ padding: "6px 9px", fontSize: 11, color: C.sub }}>↑↓ to choose · Tab to complete</div>
    </div>}
  </div>;
}
